import React from 'react';
import { fmtDate, fmtPrazo, getDaysUntil } from '../utils/formatters';
import { isAdjustmentDue } from '../utils/adjustmentVerification';
import { TONE_BADGE, TONE_BORDER, TONE_TEXT } from '../utils/statusColors';
import { contornoDoSinal } from './shared/lista/PecasDaLista';
import { Carga, EstadoDaLista, textos } from '../design';
import InadimplenciaPorFaixa from './dashboard/InadimplenciaPorFaixa';
import { FaixaDeAtraso } from '../utils/filaDeCobranca';
import EvolucaoDoFaturamento from './dashboard/EvolucaoDoFaturamento';
import RecebimentosDoMes from './dashboard/RecebimentosDoMes';
import { alertasQuePedemAcao } from '../utils/alertasDoDashboard';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';

/**
 * O Dashboard. Extraído do App.tsx sem mudar nada (#378); a carga e a casca vieram depois (08/10/2026).
 *
 * ⛔ Os números daqui são SOMAS do que a carga principal do App trouxe (assinaturas, clientes, leads, produtos). Sem
 *    essa carga ter dado certo uma vez, não há o que somar: o cartão diz "—", e não "R$ 0,00", "0", "Quitado ✓" ou
 *    "Nenhum alerta", que eram afirmações sobre dado que a tela não leu. As listas (top 5, pipeline, reajustes) passam
 *    pelo EstadoDaLista, que diz "não consegui carregar" e oferece "Tentar de novo".
 *    Numa RECARGA que falha, os números ficam os da última carga boa, e as listas dizem de quando são.
 */

// Cópia literal do `fmt` do App.tsx (função de módulo lá, sem export; importar do App faria um ciclo).
const fmt = (v: any) => { const n = parseFloat(String(v)); return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };

// A casca da Central (guia §5c): cartão, cabeçalho de cartão e o ícone do cabeçalho.
const CARTAO = 'bg-central-cartao rounded-central border border-central-borda';
const CABECALHO = 'p-4 border-b border-central-borda flex items-center gap-3';
const ICONE_DO_CABECALHO = 'p-2 bg-central-elevado rounded-lg';

interface DashboardTabProps {
  contracts: any[];
  clients: any[];
  leads: any[];
  products: any[];
  totalMRR: number;
  activeContracts: number;
  activeClients: number;
  activeProducts: number;
  canViewFinancialTotals: boolean;
  isViewer: boolean;
  canSeeReajustes: boolean;
  getClientMRR: (id: number) => number;
  /** Não é mais lida: o "Pipeline por Estágio" saiu (G1, 23/10/2026). Fica na interface para o App não mudar junto. */
  leadStatuses?: string[];
  mrrUplift: { total: number; provisorio: number; erro?: string | null };
  setSection: (s: string) => void;
  /** A2/K1 (02/11/2026): o cartão Inadimplência abre a Cobrança já na faixa de atraso clicada (`null` = todas). */
  onAbrirCobranca?: (faixa: FaixaDeAtraso | null) => void;
  /** A carga principal do App (assinaturas, clientes, leads, produtos), de onde saem os números daqui. */
  carga: Pick<Carga, 'estado' | 'erro' | 'atualizadoEm' | 'faltaram'>;
  onTentarDeNovo: () => void;
  Icon: React.FC<{ name: string; className?: string }>;
}

export default function DashboardTab({
  contracts,
  clients,
  leads,
  products,
  totalMRR,
  activeContracts,
  activeClients,
  activeProducts,
  canViewFinancialTotals,
  isViewer,
  canSeeReajustes,
  getClientMRR,
  mrrUplift,
  setSection,
  onAbrirCobranca,
  carga,
  onTentarDeNovo,
  Icon,
}: DashboardTabProps) {
    // Recolhido SEMPRE ao abrir (09/10/2026): sem localStorage, de propósito. Estado lembrado por pessoa seria mais uma
    // coisa que a tela guarda sem dizer.
    const [reajustesAbertos, setReajustesAbertos] = React.useState(false);
    // Cálculos do Dashboard
    // Função para calcular P&S pendente de um contrato
    const calcPendingPSDash = (contract: any) => {
      const items = typeof contract.items === 'string' ? JSON.parse(contract.items || '[]') : (contract.items || []);
      // Incluir todos os itens que NÃO são MRR (Serviço, PS, etc)
      const psItems = items.filter((i: any) => i.type !== 'MRR');
      return psItems.reduce((sum: number, item: any) => {
        const total = parseFloat(String(item.totalValue)) || 0;
        // Se installments é 0 ou undefined, tratar como 1 parcela única
        const installments = Math.max(1, item.installments || 1);
        const paid = item.paidInstallments || 0;
        const remaining = Math.max(0, installments - paid);
        const pendingValue = (total / installments) * remaining;
        return sum + pendingValue;
      }, 0);
    };
    const totalPS = contracts.filter((c: any) => c.status === 'Ativo').reduce((s: number, c: any) => s + calcPendingPSDash(c), 0);
    const mrrAnual = totalMRR * 12;

    // Faturamento Esperado do Mês
    // - Se tem pendingTotalValue (fatura criada): usa o valor da fatura (pode ser parcial ou ter CDR)
    // - Se não tem pendingTotalValue: usa o MRR da assinatura (vai faturar cheio)
    const faturamentoEsperado = contracts
      .filter((c: any) => c.status === 'Ativo')
      .reduce((s: number, c: any) => {
        if (c.pendingTotalValue !== null && c.pendingTotalValue !== undefined) {
          return s + (parseFloat(String(c.pendingTotalValue)) || 0);
        }
        return s + (parseFloat(String(c.mrr)) || 0);
      }, 0);
    const diferencaFaturamento = faturamentoEsperado - totalMRR;

    // Contratos vencendo em 30 dias
    // ⛔ O campo é `contractEndDate` (23/10/2026): com `endDate`, que não existe, "vencendo" e "vencidos" davam SEMPRE 0, e
    // a seção "Assinaturas Vencendo" nunca aparecia (no banco havia 2 vencendo em 30 dias e 3 vencidos).
    const contractsEndingSoon = contracts.filter((c: any) => {
      if (c.status !== 'Ativo' || !c.contractEndDate) return false;
      const days = getDaysUntil(c.contractEndDate);
      return days !== null && days > 0 && days <= 30;
    });

    // Contratos vencidos
    const contractsExpired = contracts.filter((c: any) => {
      if (c.status !== 'Ativo' || !c.contractEndDate) return false;
      const days = getDaysUntil(c.contractEndDate);
      return days !== null && days <= 0;
    });

    // Reajustes próximos
    const adjustmentsSoon = contracts.filter((c: any) => c.status === 'Ativo' && c.nextAdjustmentDate && getDaysUntil(c.nextAdjustmentDate) !== null && (getDaysUntil(c.nextAdjustmentDate) as number) <= 30);

    // Pipeline CRM - baseado nos leads ativos (excluindo Ganho, Perdido e Arquivado)
    const activeLeads = leads.filter((l: any) => !['Perdido', 'Ganho', 'Arquivado', 'Congelado'].includes(l.status));
    const pipelineMRR = activeLeads.reduce((sum: number, lead: any) => sum + (parseFloat(lead.estimatedValue) || 0), 0);
    const pipelinePS = activeLeads.reduce((sum: number, lead: any) => sum + (parseFloat(lead.estimatedValuePS) || 0), 0);

    // Top clientes por MRR
    const clientsWithMRR = clients.filter((c: any) => c.status === 'Ativo').map((c: any) => ({
      ...c,
      mrr: getClientMRR(c.id)
    })).filter((c: any) => c.mrr > 0).sort((a: any, b: any) => b.mrr - a.mrr).slice(0, 5);


    // Há fonte para os números? Só depois que a carga principal deu certo uma vez.
    const lido = carga.atualizadoEm !== null;
    const qtd = (n: number) => (lido ? String(n) : textos.numeroSemFonte);
    const reais = (v: number) => (lido ? `R$ ${fmt(v)}` : textos.numeroSemFonte);
    // ⛔ PAINEL SEM TOM (09/10/2026, rodada 2): dez cartões com o MRR em verde e o P&S em teal eram a paleta "fugindo do
    // sistema". O número do cartão é texto; o RÓTULO diz o que ele é. Tom só no DELTA ("vs MRR", marcado data-delta) e no
    // total de seção (Recebimentos). O tom de valor (guia §12) vale para um valor isolado numa linha, não para um painel.
    // Alertas (A1, Heitor 23/10/2026): só o que pede ação. Contava "reajustes" de data no passado (108 de 110 eram o chute
    // de cadastro de contrato não conferido, que nunca andou) e somava vencimento por um campo que não existe.
    const alertas = alertasQuePedemAcao(contracts);
    const resumoDosAlertas = alertas.partes.join(' • ');

    // Reajustes: os números da linha recolhida e a lista, na ordem da data (o mais antigo primeiro).
    const devidos = contracts.filter((c: any) => isAdjustmentDue(c, getDaysUntil(c.nextAdjustmentDate))).length;
    const aConferir = contracts.filter((c: any) => c.status === 'Ativo' && (c.adjustmentVerificationStatus || 'nao_conferido') === 'nao_conferido').length;
    const listaDosReajustes = [...adjustmentsSoon].sort((a: any, b: any) => String(a.nextAdjustmentDate).localeCompare(String(b.nextAdjustmentDate)));
    const maisAntigo = listaDosReajustes[0]?.nextAdjustmentDate || null;

    return (
    <div className='space-y-6'>
      {/* Cards Principais */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
        {canViewFinancialTotals && (
        <div className={`${CARTAO} p-4`} data-cartao>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-central-texto-fraco text-sm'>MRR Total</p>
              <p className='text-2xl font-bold mt-1 text-central-texto'>{reais(totalMRR)}</p>
              {lido && <p className='text-central-texto-fraco text-xs mt-1'>Anual: <span className='text-central-texto'>{reais(mrrAnual)}</span></p>}
            </div>
          </div>
        </div>
        )}
        {!isViewer && (
        <div className={`${CARTAO} p-4`} data-cartao>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-central-texto-fraco text-sm'>Pipeline CRM</p>
              <p className='text-2xl font-bold text-central-texto mt-1'>{qtd(activeLeads.length)}</p>
              {lido && <p className='text-central-texto-fraco text-xs mt-1'>{activeLeads.length === 1 ? 'oportunidade ativa' : 'oportunidades ativas'}</p>}
            </div>
          </div>
          {canViewFinancialTotals && (
          <div className='flex gap-4 mt-3 pt-3 border-t border-central-borda'>
            <div>
              <p className='text-[10px] text-central-texto-fraco uppercase'>MRR Potencial</p>
              <p className='text-sm font-bold text-central-texto'>{reais(pipelineMRR)}</p>
            </div>
            <div>
              <p className='text-[10px] text-central-texto-fraco uppercase'>P&S Potencial</p>
              <p className='text-sm font-bold text-central-texto'>{reais(pipelinePS)}</p>
            </div>
          </div>
          )}
        </div>
        )}
        {!isViewer && (
        <div className={`${CARTAO} p-4`} data-cartao>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-central-texto-fraco text-sm'>Assinaturas Ativas</p>
              <p className='text-2xl font-bold text-central-texto mt-1'>{qtd(activeContracts)}</p>
              {lido && <p className='text-central-texto-fraco text-xs mt-1'>{clients.filter((c: any) => c.status === 'Ativo').length} clientes ativos</p>}
            </div>
          </div>
        </div>
        )}
        {!isViewer && (
        <div className={`${CARTAO} p-4`} data-cartao>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-central-texto-fraco text-sm'>Alertas</p>
              <p className='text-2xl font-bold text-central-texto mt-1' data-alertas='total'>{qtd(alertas.total)}</p>
              {lido && <p className='text-central-texto-fraco text-xs mt-1'>{resumoDosAlertas || 'Nenhum alerta'}</p>}
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Segunda linha de cards */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        {canViewFinancialTotals && (
        // O contorno pelo sinal da diferença contra o MRR (S1, Heitor 23/10/2026): a diferença alterna, então a barra é verde
        // acima do MRR e vermelha abaixo; o texto da diferença fica neutro, porque quem diz o estado é a barra.
        <div className={`${CARTAO} p-4 ${lido ? contornoDoSinal(diferencaFaturamento) : ''}`} data-cartao data-sinal={lido ? Math.sign(diferencaFaturamento) : undefined}>
          <p className='text-central-texto-fraco text-sm'>Faturamento Esperado</p>
          <p className='text-xl font-bold text-central-texto mt-1'>{reais(faturamentoEsperado)}</p>
          {lido && (
          <p className='text-xs mt-0.5 text-central-texto-fraco' data-delta>
            {diferencaFaturamento >= 0 ? '+' : ''}{fmt(diferencaFaturamento)} vs MRR
          </p>
          )}
        </div>
        )}
        {canViewFinancialTotals && (
        <div className={`${CARTAO} p-4`} data-cartao>
          <p className='text-central-texto-fraco text-sm'>P&S Pendente</p>
          <p className='text-xl font-bold mt-1 text-central-texto'>
            {!lido ? textos.numeroSemFonte : totalPS > 0 ? `R$ ${fmt(totalPS)}` : 'Quitado ✓'}
          </p>
          {lido && <p className='text-central-texto-fraco text-xs'>a cobrar em faturas</p>}
        </div>
        )}
        <div className={`${CARTAO} p-4`} data-cartao>
          <p className='text-central-texto-fraco text-sm'>Clientes Ativos</p>
          <p className='text-xl font-bold text-central-texto mt-1'>{qtd(activeClients)}</p>
          {lido && <p className='text-central-texto-fraco text-xs'>{clients.length} total</p>}
        </div>
        <div className={`${CARTAO} p-4`} data-cartao>
          <p className='text-central-texto-fraco text-sm'>Produtos Ativos</p>
          <p className='text-xl font-bold text-central-texto mt-1'>{qtd(activeProducts)}</p>
          {lido && <p className='text-central-texto-fraco text-xs'>{products.length} total</p>}
        </div>
        {canViewFinancialTotals && (
        <div className={`${CARTAO} p-4`} data-cartao>
          <p className='text-central-texto-fraco text-sm'>Ticket Médio MRR</p>
          <p className='text-xl font-bold mt-1 text-central-texto'>{reais(activeContracts > 0 ? totalMRR / activeContracts : 0)}</p>
          {lido && <p className='text-central-texto-fraco text-xs'>por assinatura</p>}
        </div>
        )}
      </div>

      {/* Recebimentos do Mês (R1, Heitor 23/10/2026): pela base dos pagamentos, com carga própria. */}
      {canViewFinancialTotals && <RecebimentosDoMes />}

      {/* Inadimplência por faixa (X1, Heitor 23/10/2026): o vencido em aberto da carteira inteira. */}
      {canViewFinancialTotals && <InadimplenciaPorFaixa onAbrir={onAbrirCobranca || (() => setSection('cobranca'))} />}

      {!isViewer && <div className={`grid grid-cols-1 ${canViewFinancialTotals ? 'lg:grid-cols-2' : ''} gap-6`}>
        {/* Top Clientes por MRR - apenas para admin */}
        {canViewFinancialTotals && (
        <div className={`${CARTAO} overflow-hidden`}>
          <div className={CABECALHO}>
            <div className={ICONE_DO_CABECALHO}>
              <Icon name='chart' className='w-5 h-5 text-central-texto-fraco' />
            </div>
            <div>
              <h3 className='text-central-texto font-semibold'>Top 5 Clientes por MRR</h3>
              <p className='text-central-texto-fraco text-sm'>Maior receita recorrente</p>
            </div>
          </div>
          <div className='p-4'>
            <EstadoDaLista
              carga={carga}
              temDados={clientsWithMRR.length > 0}
              oQue='os clientes'
              onTentarDeNovo={onTentarDeNovo}
              vazio={<p className='text-central-texto-fraco text-center py-8'>Nenhum cliente com MRR</p>}
            >
              <div className='space-y-3'>
                {clientsWithMRR.map((c: any, idx: number) => {
                  const percentage = totalMRR > 0 ? (c.mrr / totalMRR) * 100 : 0;
                  return (
                    <div key={c.id} className='flex items-center gap-4'>
                      {/* A posição é estado: carrega peso, não cor (guia §5b). */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm bg-central-elevado border border-central-borda text-central-texto ${idx === 0 ? 'font-bold' : 'font-medium'}`}>
                        {idx + 1}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='font-medium text-central-texto truncate'>{c.name || c.tradeName}</p>
                        <div className='flex items-center gap-2 mt-1'>
                          <div className='flex-1 h-2 bg-central-elevado rounded-full overflow-hidden'>
                            <div className='h-full bg-green-500 rounded-full' style={{ width: `${percentage}%` }}></div>
                          </div>
                          <span className='text-xs text-central-texto-fraco'>{percentage.toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className='text-right'>
                        <p className={`font-semibold ${TONE_TEXT.open}`}>R$ {fmt(c.mrr)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </EstadoDaLista>
          </div>
        </div>
        )}

        {/* A evolução do faturamento (G1 e M1, Heitor 23/10/2026) no lugar do "Pipeline por Estágio", que repetia o funil do CRM
            e não era usado. Só para quem vê os totais financeiros, como o Top 5 ao lado. */}
        {canViewFinancialTotals && <EvolucaoDoFaturamento mrrDeHoje={lido ? totalMRR : null} />}
      </div>}

      {/* Assinaturas Vencendo */}
      {!isViewer && (contractsEndingSoon.length > 0 || contractsExpired.length > 0) && (
        <div className={`${CARTAO} overflow-hidden`}>
          <div className={CABECALHO}>
            <div className={ICONE_DO_CABECALHO}>
              <Icon name='clock' className='w-5 h-5 text-central-texto-fraco' />
            </div>
            <div>
              <h3 className='text-central-texto font-semibold'>Assinaturas Vencendo</h3>
              <p className='text-central-texto-fraco text-sm'>{contractsExpired.length} vencidos • {contractsEndingSoon.length} nos próximos 30 dias</p>
            </div>
          </div>
          <div className='p-4'>
            <div className='space-y-3'>
              {[...contractsExpired, ...contractsEndingSoon].sort((a: any, b: any) => String(a.contractEndDate).localeCompare(String(b.contractEndDate))).slice(0, 5).map((c: any) => {
                const cl = clients.find((x: any) => x.id === c.clientId);
                const days = getDaysUntil(c.contractEndDate);
                const isExpired = days !== null && days <= 0;
                const tom = isExpired ? 'danger' : 'warning';
                return (
                  <div key={c.id} className={`flex items-center justify-between p-4 rounded-lg border bg-central-elevado ${TONE_BORDER[tom]}`}>
                    <div className='flex items-center gap-4'>
                      <Icon name='clipboard' className={`w-5 h-5 ${TONE_TEXT[tom]}`} />
                      <div>
                        <p className='font-semibold text-central-texto'>{nomeDeExibicao(cl, '—')}</p>
                        <p className='text-sm text-central-texto-fraco'>Assinatura #{c.id} • MRR: <span className={TONE_TEXT.open}>R$ {fmt(c.mrr)}</span></p>
                      </div>
                    </div>
                    <div className='flex items-center gap-3'>
                      <div className='text-right'>
                        <p className='text-sm font-medium text-central-texto'>{fmtDate(c.contractEndDate)}</p>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${TONE_BADGE[tom]}`}>
                          {isExpired ? 'Vencido!' : `${days} dias`}
                        </span>
                      </div>
                      <button onClick={() => { setSection('contracts'); }} className='p-2 text-central-texto-fraco hover:text-central-texto hover:bg-central-cartao rounded-lg' title='Ver assinatura'>
                        <Icon name='eye' className='w-5 h-5' />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Reajustes (rodada 2, 09/10/2026): RECOLHIDOS sempre ao abrir, numa linha. A lista ficava aberta com doze, vinte,
          cento e vinte linhas de borda vermelha, relógio vermelho e "Vencido!": vermelho igual em tudo é papel de parede. Os
          números da linha contam conjuntos DIFERENTES e não se somam: "devidos" é regra conferida com a data vencida; "a
          conferir" é toda assinatura sem a regra lida, em qualquer data; a lista é quem tem data nos próximos 30 dias ou
          vencida. Por isso a lista tem rótulo próprio ao expandir. */}
      {canSeeReajustes && <div className={`${CARTAO} overflow-hidden`}>
        <div className={CABECALHO}>
          <div className={ICONE_DO_CABECALHO}>
            <Icon name='bell' className='w-5 h-5 text-central-texto-fraco' />
          </div>
          <div className='min-w-0'>
            <h3 className='text-central-texto font-semibold'>Reajustes</h3>
            <p className='text-central-texto-fraco text-sm' data-reajustes='resumo'>
              {lido
                ? [
                  `${devidos} ${devidos === 1 ? 'devido' : 'devidos'}`,
                  `${aConferir} a conferir`,
                  // A leitura do aumento que falha diz (era omitido, como se fosse zero).
                  mrrUplift.erro ? 'aumento previsto não lido' : mrrUplift.total > 0 && `+R$ ${fmt(mrrUplift.total)}/mês`,
                  maisAntigo && `o mais antigo desde ${fmtDate(maisAntigo)}`,
                ].filter(Boolean).join(' · ')
                : textos.numeroSemFonte}
            </p>
          </div>
          <div className='ml-auto flex items-center gap-2'>
            <button
              type='button'
              onClick={() => setReajustesAbertos((v) => !v)}
              aria-expanded={reajustesAbertos}
              className='px-3 py-1.5 text-sm text-central-texto-fraco hover:text-central-texto rounded-lg whitespace-nowrap'
            >
              {reajustesAbertos ? 'Recolher' : 'Ver a lista'}
            </button>
            <button onClick={() => setSection('reajustes')} className='px-3 py-1.5 text-sm bg-central-cartao border border-central-borda text-central-texto hover:bg-central-elevado rounded-lg whitespace-nowrap'>Abrir Reajustes</button>
          </div>
        </div>
        {reajustesAbertos && <div className='p-4'>
          <EstadoDaLista
            carga={carga}
            temDados={adjustmentsSoon.length > 0}
            oQue='as assinaturas'
            onTentarDeNovo={onTentarDeNovo}
            vazio={<p className='text-central-texto-fraco text-center py-8'>Nenhum reajuste nos próximos 30 dias</p>}
          >
            <p className='text-xs text-central-texto-fraco mb-3' data-reajustes='rotulo-da-lista'>{adjustmentsSoon.length} com data nos próximos 30 dias ou vencida</p>
            <div className='space-y-2'>
              {listaDosReajustes.map((c: any) => {
                const cl = clients.find((x: any) => x.id === c.clientId);
                // A linha leva a Reajustes, onde a anualidade se resolve (o ofício sai de lá).
                return (
                  <button
                    type='button'
                    key={c.id}
                    onClick={() => setSection('reajustes')}
                    title='Abrir em Reajustes'
                    className='w-full text-left flex items-center justify-between px-4 py-3 rounded-lg border border-central-borda bg-central-elevado hover:bg-central-cartao'
                  >
                    <div className='min-w-0'>
                      <p className='font-semibold text-central-texto truncate'>{nomeDeExibicao(cl, '—')}</p>
                      <p className='text-sm text-central-texto-fraco'>Assinatura #{c.id}{c.contractNumber ? ` • Contrato ${c.contractNumber}` : ''} • {c.adjustmentIndex} • MRR: <span className={TONE_TEXT.open}>R$ {fmt(c.mrr)}</span></p>
                    </div>
                    <div className='text-right shrink-0 ml-4'>
                      <p className='text-sm font-medium text-central-texto'>{fmtDate(c.nextAdjustmentDate)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TONE_BADGE.neutral}`}>{fmtPrazo(getDaysUntil(c.nextAdjustmentDate))}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </EstadoDaLista>
        </div>}
      </div>}
    </div>
  );
}
